"use client";

import React, { ForwardRefExoticComponent, RefAttributes } from "react";
import { EmblaOptionsType } from "embla-carousel";
import { DotButton, useDotButton } from "./DotButton";
import { PrevButton, NextButton, usePrevNextButtons } from "./ArrowButtons";
import useEmblaCarousel from "embla-carousel-react";
import Section from "../layout/Section";
import Autoplay from "embla-carousel-autoplay";
import { LucideProps } from "lucide-react";

export type CarouselSlide = {
  title: string;
  description: string;
  Icon: ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>
  >;
};

type PropType = {
  slides: CarouselSlide[];
  options?: EmblaOptionsType;
  autoplay?: boolean;
};

const Carousel: React.FC<PropType> = ({ slides, options, autoplay }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    options,
    autoplay ? [Autoplay()] : []
  );

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  return (
    <Section>
      <div className="embla">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="embla__container">
            {slides.map(({ title, description, Icon }) => (
              <div className="embla__slide" key={title}>
                <div className="bg-secondary-light relative block h-full space-y-8 rounded-2xl p-6 transition-colors duration-700 select-none md:p-10 xl:p-14">
                  <Icon className="text-accent h-20 w-20" strokeWidth={0.5} />
                  <h3 className="text-xl md:text-2xl xl:text-3xl">{title}</h3>
                  <p className="md:text-lg">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="embla__controls">
          <div className="embla__buttons">
            <PrevButton
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
            />
            <NextButton
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
            />
          </div>

          <div className="embla__dots">
            {scrollSnaps.map((_, index) => (
              <DotButton
                key={index}
                onClick={() => onDotButtonClick(index)}
                className={"embla__dot".concat(
                  index === selectedIndex ? "embla__dot--selected" : ""
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
};

export default Carousel;
